import { useCallback, useEffect, useMemo } from 'react';
import { captureException } from '@sentry/nextjs';
import { CURRENCY_USD } from 'constants/currency';
import { useGetCoachPremiumContentLazyQuery } from 'types/generated/client';
import { UploadProvider } from 'context/UploadVideoContext';
import { useModal } from 'hooks/useModal';
import { useViewer } from 'hooks/useViewer';
import ConfigurePremium from 'components/Premium/ConfigurePremium';
import SetPriceModal from 'components/Premium/SetPriceModal';
import UploadContentModal from 'components/Premium/UploadContentModal';

const PremiumCoachSide = () => {
  const viewer = useViewer();

  const uploadContentModal = useModal();
  const setPriceModal = useModal();

  const [
    getCoachPremiumContent,
    { data: premiumCoachContent, loading: premiumCoachContentLoading },
  ] = useGetCoachPremiumContentLazyQuery();

  const price = premiumCoachContent?.userProfiles?.[0]?.priceUnitAmountPremiumContentDefault || 0;

  const description = premiumCoachContent?.userProfiles[0].premiumContentDescription || '';

  const subscribers = premiumCoachContent?.userContentBetaSubscriptions.length || 0;

  const isSubscriptionDisabled = !price;

  const priceFormInitialValues = useMemo(
    () => ({
      price,
      description,
    }),
    [premiumCoachContent],
  );

  const handleGetCoachPremiumContent = useCallback(async () => {
    try {
      await getCoachPremiumContent({
        variables: {
          coachId: viewer.userId,
        },
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
      });
    } catch (err) {
      captureException(err);
    }
  }, [viewer.userId, getCoachPremiumContent]);

  useEffect(() => {
    viewer.userId && handleGetCoachPremiumContent();
  }, [handleGetCoachPremiumContent, viewer.userId]);

  return (
    <>
      <ConfigurePremium
        standalone={false}
        handleEdit={setPriceModal.openModal}
        handleUpload={uploadContentModal.openModal}
        price={price || 0}
        isSubscriptionDisabled={isSubscriptionDisabled}
        subscribers={subscribers}
        loading={premiumCoachContentLoading}
        currency={
          premiumCoachContent?.userProfiles?.[0]?.stripeMerchantCurrency ||
          premiumCoachContent?.userProfiles?.[0]?.currencyCoachDefault ||
          CURRENCY_USD
        }
      />
      <UploadProvider>
        <UploadContentModal
          open={uploadContentModal.isOpen}
          handleClose={uploadContentModal.closeModal}
          onSuccessUpload={handleGetCoachPremiumContent}
        />
      </UploadProvider>
      <SetPriceModal
        open={setPriceModal.isOpen}
        initialValues={priceFormInitialValues}
        handleClose={setPriceModal.closeModal}
        onSuccess={handleGetCoachPremiumContent}
        currency={
          premiumCoachContent?.userProfiles?.[0]?.stripeMerchantCurrency ||
          premiumCoachContent?.userProfiles?.[0]?.currencyCoachDefault ||
          CURRENCY_USD
        }
      />
    </>
  );
};

export default PremiumCoachSide;
